import { useParams, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { shopIcpBySlug } from '@evi/store-kit';
import { FilmHero } from '@/components/FilmHero';
import { NotFoundPage } from './NotFoundPage';
import { BecomePartnerButton } from '@/components/InstallerBand';
import { PARTNERS_APPLY_URL } from '@/lib/partnerLinks';
import styles from './WhyPage.module.css';

/** Shop "who it's for" page (/why/:icp): the ICP's UGC film hero + copy + two
 *  quote cards + a CTA into the matching shop category. The electrician page
 *  adds the SP capture and links the qualification form. */
export function WhyPage() {
  const { icp = '' } = useParams<{ icp: string }>();
  const content = shopIcpBySlug(icp);

  if (!content) return <NotFoundPage />;

  return (
    <div className={styles.page}>
      <FilmHero video={content.video} poster={content.poster} framing={content.framing} label={content.title}>
        <p className={styles.eyebrow}>EV Initiative</p>
        <h1 className={styles.title}>{content.title}</h1>
        {/* The call to action rides in the hero under the headline (the same
            one closes the page below). */}
        <Link to={`/${content.ctaSlug}`} className={`eviBtn eviBtnPrimary ${styles.heroCta}`}>
          {content.ctaLabel} <ArrowRight size={16} />
        </Link>
      </FilmHero>

      <div className={styles.body}>
        <p className={styles.lead}>{content.body}</p>

        {content.quotes.map((q) => (
          <figure key={q.name} className={styles.quote}>
            <blockquote className={styles.quoteText}>&ldquo;{q.text}&rdquo;</blockquote>
            <figcaption className={styles.quoteName}>{q.name}</figcaption>
          </figure>
        ))}

        {content.salesPartner && <ElectricianSp />}

        <Link to={`/${content.ctaSlug}`} className="eviBtn eviBtnPrimary">
          {content.ctaLabel} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

/** Electrician SP section: "See if I qualify" and the installer band's
 *  "Become a Sales Partner" button, both to the Sales Partner application on
 *  evinitiative.com (the quick email capture it had is gone, Warren 9 Oct 2026). */
function ElectricianSp() {
  return (
    <section className={styles.spCard}>
      <h2 className={styles.spHeading}>Become an EVI Sales Partner</h2>
      <p className={styles.spBody}>
        The only EV charging network that creates passive income for its partners. Earn a
        commission on the hardware you sell and a share of every charging session on chargers you
        bring to the network, paid out through Stripe.
      </p>

      <a href={PARTNERS_APPLY_URL} className="eviBtn eviBtnPrimary">
        See if I qualify
      </a>

      <BecomePartnerButton />
    </section>
  );
}
